import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";

import { GROUNDLIGHT_SUPPORT_EMAIL } from "config/constants";
import { useAppSelector } from "hooks/store";
import { InfoTooltip } from "new-components/infoTooltip/InfoTooltip";
import { Typography } from "new-components/typography/Typography";
import { AccountSectionTitle, LabelType } from "pages/account/AccountPage";
import { getUserState } from "store/slices/Auth/authSlice";
import { User } from "types/users";

interface AccountInfoProps {
  user: User | undefined;
}

function AccountInfoDataLabel(props: { text: string }) {
  const { text } = props;
  return <Typography sx={{ fontSize: "14px", lineHeight: "24px", color: "#919599" }}>{text}</Typography>;
}

export function AccountInfoData(props: { text: string }) {
  const { text } = props;
  return <Typography sx={{ fontSize: "18px", lineHeight: "24px", color: "#000" }}>{text}</Typography>;
}

const AccountInfoItem = (props: { label: string; value: string }) => {
  const { label, value } = props;
  return (
    <Box>
      <AccountInfoDataLabel text={label} />
      <Box sx={{ padding: "5px 10px" }}>
        <AccountInfoData text={value} />
      </Box>
    </Box>
  );
};

export const AccountInfoTooltip = () => {
  const { account_labeling_settings: accountLabelingSettings } = useAppSelector(getUserState);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <InfoTooltip
      placement={isMobile ? "bottom" : "right-start"}
      infoContentTitle="Labeling resources"
      infoContent={
        <>
          <Typography>
            When queries are escalated to cloud labelers, your account is set up to use the default labeling resource
            configuration:
          </Typography>
          <Box sx={{ display: "flex", paddingLeft: "10px", gap: "10px" }}>
            <Typography>•</Typography>
            <Typography>
              <b>{LabelType[accountLabelingSettings]}</b>
            </Typography>
          </Box>
          <Typography>
            To change this labeling resource configuration, please contact{" "}
            <Link to={`mailto:${GROUNDLIGHT_SUPPORT_EMAIL}`} style={{ color: "#0450BA", fontWeight: "500" }}>
              {GROUNDLIGHT_SUPPORT_EMAIL}
            </Link>
            .
          </Typography>
        </>
      }
    />
  );
};

export function AccountInfo(props: AccountInfoProps) {
  const { user } = props;

  const accountInfoItems = [
    { label: "Username:", value: user?.username || "N/A" },
    { label: "Company:", value: user?.userprofile.company || "N/A" },
    { label: "Email:", value: user?.email || "N/A" },
  ];
  const groupName = user?.groups?.[0]?.name || "N/A";

  return (
    <Stack sx={{ backgroundColor: "white", borderRadius: "10px", padding: "10px 20px", height: "100%" }}>
      <AccountSectionTitle text="My Account" />
      <Stack sx={{ padding: "10px 20px", gap: "5px" }}>
        {accountInfoItems.map(({ label, value }) => (
          <AccountInfoItem label={label} value={value} key={label} />
        ))}

        <Box>
          <AccountInfoDataLabel text="Group:" />
          <Box sx={{ padding: "5px 10px", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <AccountInfoData text={groupName} />
            {user?.enable_account_invite ? (
              <Link to="/reef/my-account/members">
                <Typography
                  sx={{
                    fontSize: "14px",
                    lineHeight: "24px",
                    color: "#006DD9",
                    fontWeight: 500,
                    textDecoration: "underline",
                  }}
                >
                  Users ({user.account_members ?? "N/A"})
                </Typography>
              </Link>
            ) : null}
          </Box>
        </Box>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <AccountInfoDataLabel text="Labeling resources:" />
            <AccountInfoTooltip />
          </Box>
          <Box sx={{ padding: "5px 10px" }}>
            <AccountInfoData text={user ? LabelType[user.account_labeling_settings] : "N/A"} />
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
}
